<!-- PatientHeader.vue -->
<!-- NOTE: This will be on legacy pages so don't use Bootstrap! -->
<script setup lang="ts">
    import * as choiceApi from '../../../lib/ChoiceApi'
    import _ from 'lodash';
    import { onMounted, reactive, computed, watch, ref, nextTick, StyleValue, RendererElement } from 'vue';
    import { AppScreens, DatabaseIdTypes, PatientSetupTabs, PortalType, MMLConnectionType, UserSettingGroup } from '../../../Enums/ChoiceEnums';
    import { NoTzDate } from '../../../lib/DateUtils';
    import { PatientHeaderBasicInfoModel, PatientCareTeamMemberModel } from '../../../lib/Api/Patient';
    import { timeout } from '../../../lib/ChoiceUtils';
    import { IconTypes, ModalParent } from '../../../Enums/UiEnums';
    import { patientSetupScreenUrl, patientFlagAssignmentScreenUrl } from '../../../lib/OfficeMdUrls';
    import { setVueModal } from '../../../lib/Legacy/ModalWindow';
    import { openWindowFromVue } from '../../../lib/Models/CommonItemsModel';    
    import { ImageType } from '../../../lib/OfficeMd/images';
    import { PatientAppointmentInfo } from '../../../lib/Api/Schedule';
    import { initPinia } from '../../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../../lib/Stores/PrivilegesStore';
    import { usePatientFlagsStore } from '../../../lib/Stores/PatientFlagsStore';
    import { usePatientsStore } from '../../../lib/Stores/PatientsStore';
    import { useUserSettingsStore } from '../../../lib/Stores/UserSettingsStore'; 
    import { IPatientFlagDisplayOrder, IPatientFlagPatientAssignItem  } from '../../../lib/Models/Setup/PatientFlagSetupModel';
    import GenderIcon from './GenderIcon.vue'
    import CKCCIcon from './CKCCIcon.vue';
    import MMLIcon from './MMLIcon.vue';
    import CareTeamMembersToolTip from './CareTeamMembersToolTip.vue';
    import PatientBasicInfoToolTip from './PatientBasicInfoToolTip.vue';
    import Icon from '../../Icon.vue';
    import ImageIcon from '../../Icons/ImageIcon.vue';
    import XSpan from '../../HtmlWrapper/XSpan.vue';
    import PatientFlagBadges from './PatientFlagBadges.vue';

    const privilegesStore = usePrivilegesStore(initPinia());    
    const patientFlagsStore = usePatientFlagsStore(initPinia());
    const patientsStore = usePatientsStore(initPinia());
    const userSettingsStore = useUserSettingsStore(initPinia());

    const props = withDefaults(defineProps<{
        displayContext: PortalType,
        patientEntityId?: number,
        scheduleDate?: NoTzDate,
        appointmentId?: number,
        trickleImportUnderway?: boolean,
        hoverToolTipSelector?: string,
        mouseOutDelayMilliseconds?: number,  // How long after we stop hovering before we close the tool tip
        legacyPage?: boolean,        // Are we displaying on a legacy page (impacts how things are rendered)
        fontSize?: "smaller"|"default",
        tooltipRightOffset?: number,
        cacheHeader?: boolean
    }>(), {
        mouseOutDelayMilliseconds: 250,
        legacyPage: true,
        cacheHeader: true
    });

    defineExpose({refreshMMLConnection});

    const emit = defineEmits<{
        clickMML: [patientEid: number],
        mouseHover: [hover:boolean, toolTipStyle?: StyleValue],
        mouseOverHeader: [over: boolean]
    }>();

    type IPatientFlagPatientAssignItemWithDisplayOrder = IPatientFlagPatientAssignItem & {displayOrder?: number};

    const compData = reactive<{
        basicInfo?: PatientHeaderBasicInfoModel,
        careTeam?: PatientCareTeamMemberModel[],

        hoverPatientHeader?: boolean,
        toolTipWidth?: number,
        toolTipStyle?: StyleValue,

        patientFlagDisplayOrderValues?: IPatientFlagDisplayOrder[],
        hoverFlag?: boolean,
        hoverFlagData?: IPatientFlagPatientAssignItem,
        flagToolTipStyle?: StyleValue,
        mouseOverHeader?: boolean,
        showSetupModal?: boolean,
        appointmentInfo?: PatientAppointmentInfo,
        showPatientChartNumberInHeader: boolean,
        patientFlagBadgesKey: number,
        displayHeader?: boolean
    }>({
        showPatientChartNumberInHeader: true,
        patientFlagBadgesKey: 0
    });

    const patientFlagBadges = ref<InstanceType<typeof PatientFlagBadges>>();
    const toolTip = ref<HTMLElement | undefined | null>();
    const patientHeader = ref<HTMLElement|undefined>();

    interface PatientFlagModel
    {
        id: number,
        shortName: string,
        visibleInContainer?: boolean
    }

    onMounted(async ()=> {
        const showChartNumber = (await choiceApi.companySettings.getBoolVal(choiceApi.companySettings.Setup.PatientHeader_DisplayChartNumber));
        if (undefined !== showChartNumber)
            compData.showPatientChartNumberInHeader = showChartNumber;

        loadData();
        const enabled = await userSettingsStore.getUserSettingString(UserSettingGroup.PatientHeader, "TDISP");
        compData.displayHeader = enabled == "Y";
    });

    watch(() => props.patientEntityId, (_newVal, _oldVal) =>{
        loadData();
    });
    watch(() => props.scheduleDate, (_newVal, _oldVal) =>{
        loadData();
    });

    watch(() => props.appointmentId, (_newVal, _oldVal) => {
        loadAppointmentData();
    });

    function loadData(reloadPatientFlagDisplayOrder?: boolean)
    {
        if (undefined === props.displayContext)
        {
            alert("Invalid displayContext");
        }

        patientFlagsStore.getFlagDisplayOrder(reloadPatientFlagDisplayOrder).then(
            resp => { 
                compData.patientFlagDisplayOrderValues = resp;

                // Force the flags to re-draw
                compData.patientFlagBadgesKey = 1 + (compData.patientFlagBadgesKey ?? 0);
            });

        loadBasicPatientInfo();
        loadCareTeam(); 
        loadAppointmentData();
    }

    async function loadBasicPatientInfo() {
        compData.basicInfo = undefined;
        if (props.patientEntityId)
        {
            if (props.cacheHeader) {
                compData.basicInfo = await patientsStore.getBasicPatientInfo(props.patientEntityId, DatabaseIdTypes.entity_id, props.scheduleDate);
            } else {
                compData.basicInfo = await choiceApi.patient.getPatientHeaderBasicInfo(props.patientEntityId, DatabaseIdTypes.entity_id, props.scheduleDate);
            }
            refreshMMLConnection();
        }        
    }

    async function refreshMMLConnection() {
        if (compData.basicInfo?.patient_id)
            compData.basicInfo.mmlConnectionType = await choiceApi.MML.GetConnectionType(compData.basicInfo?.patient_id);
    }

    async function loadCareTeam()
    {
        compData.careTeam = [];
        if (props.patientEntityId)
        {
            compData.careTeam = await choiceApi.patient.getCareTeam(props.patientEntityId, DatabaseIdTypes.entity_id);
        }  
    }

    async function loadAppointmentData()
    {
        compData.appointmentInfo = undefined;
        if (props.appointmentId && props.patientEntityId)
            compData.appointmentInfo = await choiceApi.schedule.getPatientAppointmentInfo(props.appointmentId, DatabaseIdTypes.entity_id, props.patientEntityId);
    }

    const basicInfo = computed<PatientHeaderBasicInfoModel|undefined>(()=> compData.basicInfo);

    const fontSizeEm = computed<string>(()=> {
        return props.fontSize == "smaller" ? "font-size: 0.75rem" : "font-size: 0.85rem";
    });

    const imageIconClassPointer = computed<string>(()=> {
        return props.fontSize == "smaller" ? "isalus-me-1 pointer" : "isalus-me-1 pointer adjustImageIcon";
    });
    const imageIconClass = computed<string>(()=> {
        return props.fontSize == "smaller" ? "isalus-me-1" : "isalus-me-1 adjustImageIcon";
    });

    async function onClickMML()
    {
        if (props.patientEntityId)
        {
            emit("clickMML", props.patientEntityId);
            openWindowFromVue(AppScreens.MMLAdmin, `/officemd/app/mml/mmladmin/entity/${props.patientEntityId}`, { noTimestamp: "Y" });
        }
    }

    async function onClickEditFlagSetting()
    {
        onClickSetupButton();
    }

    function onClickCKCC()
    {
        if (props.patientEntityId)
        {
            // TODO: See if this is acceptable
            window.open(patientSetupScreenUrl(DatabaseIdTypes.entity_id, props.patientEntityId, PatientSetupTabs.CaseManagement));
        }
    }

    function onClickGender()
    {
        if (props.patientEntityId)
        {
            // TODO: See if this is acceptable
            window.open(patientSetupScreenUrl(DatabaseIdTypes.entity_id, props.patientEntityId, PatientSetupTabs.Demographics));
        }        
    }

    function onMouseOverPatientHeader()
    {
        // Make sure patient flag tool tip is hidden
        hidePatientFlagToolTip();

        if (!compData.displayHeader)
            return;

        compData.mouseOverHeader = true;
        emit("mouseOverHeader", compData.mouseOverHeader);

        compData.hoverPatientHeader = true;
        compData.toolTipStyle = patientHeaderToolTipStyle();

        nextTick(() =>{
            emit("mouseHover", compData.hoverPatientHeader ?? false, compData.toolTipStyle);
        });

        
    }
    async function onMouseOutPatientHeader()
    {
        compData.mouseOverHeader = false;
        emit("mouseOverHeader", compData.mouseOverHeader);

        if (props.mouseOutDelayMilliseconds && props.mouseOutDelayMilliseconds > 0)
        {
            await timeout(props.mouseOutDelayMilliseconds);
            if (compData.mouseOverHeader)
                return;
        }

        compData.hoverPatientHeader = false;
        compData.toolTipStyle = patientHeaderToolTipStyle();
        emit("mouseHover", compData.hoverPatientHeader);
    }    

    function patientHeaderToolTipStyle(): StyleValue
    {
        const boundingRect = patientHeader.value?.getBoundingClientRect();
        if (!boundingRect)
            return {};

        const rightPos = window.innerWidth - boundingRect.right - 0 + (props.tooltipRightOffset ?? 0);

        return {
            "display": compData.hoverPatientHeader ? "block" : "none",
            "position": props.legacyPage ? "fixed" : "absolute",
            "z-index": 999,
            "right": `${rightPos}px`,
            "top": `${boundingRect.bottom + window.scrollY + 2}px`,
            "border": "1px solid rgb(106, 140, 203)",
            "background-color": "rgb(241, 241, 241)",
            "margin": 5
        }
    };

    const toolTipSelector = computed<string | RendererElement | null | undefined>(() =>{
        return props.hoverToolTipSelector ?? "#internalPatientHeaderToolTip";
    });

    function onClickSetupButton()
    {
        if (!props.patientEntityId)
        {
            alert("patientEntityId it invalid");
            return;
        }             

        setVueModal(
                patientFlagAssignmentScreenUrl(DatabaseIdTypes.patient_entity_id, props.patientEntityId), 
                "Patient Flag Assign", ModalParent.launchWindow)
                    .then(() => loadData(true));
    }

    async function launchTelehealthSession()
    {
        if (!compData.basicInfo?.patient_id)
        {
            alert("Unable to get telehealth url");
            return;
        }

        window.open(await choiceApi.patient.getTelehealthUrl(compData.basicInfo?.patient_id), "_blank", "location=no, menubar=no, resizable=yes, scrollbars=no");
    }

    function hidePatientFlagToolTip()
    {
        patientFlagBadges.value?.hidePatientFlagToolTip();
    }
</script>

<style scoped>
    @import url('../../../../css/choice.css');

    .border-right {
        border-right:1px solid grey;        
    }

    .tool-tip-column {
        font-size:small        
    }

    .adjustImageIcon {
        margin-bottom:-3px;
    }

    .patient-and-flag-container {
        display: grid;
        grid-template-columns: 1fr auto; /* 1fr for left section, auto for right section */
      }
      
      .flag-badge-section {
        text-align: right;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .patient-section {
        white-space: nowrap; /* Prevent content from wrapping */
        margin-left: 5px;
      }
</style>

<template>
    
    <XSpan class="isalus-text-left" :style="fontSizeEm">
        <div class="patient-and-flag-container">
            <div class="flag-badge-section">
                <PatientFlagBadges ref="patientFlagBadges" 
                        :key="compData.patientFlagBadgesKey"
                        :patientEntityId="props.patientEntityId" 
                        :displayContext="props.displayContext" 
                        :mouseOutDelayMilliseconds="props.mouseOutDelayMilliseconds" 
                        :legacyPage="props.legacyPage"
                        :fontSize="props.fontSize"
                        @mouseOverFlag="() => compData.hoverPatientHeader = false" />
            </div>
            <div class="patient-section">
                <XSpan v-if="basicInfo" ref="patientHeader" class="isalus-section-title default-cursor" @mouseover="onMouseOverPatientHeader" @mouseout="onMouseOutPatientHeader">
                        
                    <XSpan v-if="!compData.basicInfo?.group_notes">
        
                        <Icon :icon="IconTypes.VerticalEllipsis" 
                           @click="onClickSetupButton" 
                           title="Display Setup Options" 
                           class="isalus-ms-2 isalus-me-2 pointer" />
            
                        <!-- Notifications -->
                        <ImageIcon :image="ImageType.TelehealthNotification" 
                                   v-if="compData.appointmentInfo?.isTelemedicineAppointment"
                                   :class="imageIconClassPointer"
                                   :performAction="launchTelehealthSession" />
                        <ImageIcon :image="ImageType.TrickleImportNotification"
                                    v-if="props.trickleImportUnderway"
                                    :class="imageIconClass" />

                       <!-- Indicators -->
                       <MMLIcon v-if="undefined !== basicInfo.mmlConnectionType" 
                                   :connectionType="basicInfo.mmlConnectionType" 
                                   :class="imageIconClassPointer"
                                   @click="onClickMML" />
                       <CKCCIcon v-if="basicInfo.ckccStatus" 
                                   :status="basicInfo.ckccStatus" 
                                   :tooltip="basicInfo.ckccTooltip" 
                                   :class="imageIconClassPointer" 
                                   @click="onClickCKCC" />
                    </XSpan>
                    <XSpan v-if="compData.basicInfo?.group_notes">
                        <ImageIcon :image="ImageType.GroupNoteNotification" 
                            v-if="compData.basicInfo?.group_notes"
                            :class="imageIconClass" />
                    </XSpan>
        
                    <XSpan>
                        <XSpan v-if="compData.showPatientChartNumberInHeader">{{ basicInfo.patient_id }}-</XSpan>
                        <XSpan>{{ basicInfo.displayName }} {{ basicInfo.birthDay }}</XSpan>
                    </XSpan>
                    <template v-if="basicInfo.age">
                        ({{ basicInfo.age }})
                    </template>
                    <GenderIcon 
                        :gender="basicInfo.gender" 
                        class="pointer" 
                        @click="onClickGender" />

                </XSpan>
            </div>
        </div>

    
        <template v-if="compData.hoverPatientHeader">
            <Teleport :to="toolTipSelector" :disabled="!compData.hoverPatientHeader">
                <div ref="toolTip">
                    <slot name="toolTip" :basicInfo="compData.basicInfo" :careTeam="compData.careTeam">
                        <table @mouseover="onMouseOverPatientHeader" @mouseout="onMouseOutPatientHeader">
                            <tbody>
                                <tr style="vertical-align: top">
                                    <td v-if="compData.careTeam?.length" class="border-right tool-tip-column">
                                        <CareTeamMembersToolTip :teamMembers="compData.careTeam" />
                                    </td>
                                    <td class="tool-tip-column">
                                        <PatientBasicInfoToolTip :basicInfo="basicInfo" @clickMML="onClickMML" @clickEditFlagSetting="onClickEditFlagSetting" />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </slot>
                </div>
            </Teleport>    
        </template>
    
        <div v-if="!props.hoverToolTipSelector" id="internalPatientHeaderToolTip" :style="compData.toolTipStyle"/>
    </XSpan>
</template>
